import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, LogIn, LogOut, QrCode } from 'lucide-react';
import type { ClockStatusDto } from '@workforce/shared';
import { api, ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useTz } from '@/lib/timezone';
import { Button, Card, ErrorBanner, Spinner } from '@/components/ui';
import { useClockStatus } from './MyAttendance';

type ScanResult = ClockStatusDto & { duplicate: boolean; deviceName: string };

/**
 * Opened by the phone camera from the rotating QR code on the office door tablet.
 * The code is only valid for a short time, so a photo of it cannot be reused from home.
 */
export function ScanPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('t') ?? '', [params]);
  const qc = useQueryClient();
  const { fmt } = useTz();
  const status = useClockStatus(!!token);
  const [done, setDone] = useState<ScanResult | null>(null);
  const scan = useMutation({
    mutationFn: (direction: 'in' | 'out') => api.post<ScanResult>('/me/attendance/scan', { token, direction }),
    onSuccess: (r) => {
      setDone(r);
      qc.setQueryData(['me', 'attendance', 'status'], r);
      qc.invalidateQueries({ queryKey: ['me', 'attendance', 'days'] });
    },
  });

  const expired = scan.error instanceof ApiError && scan.error.code === 'qr_expired';

  return (
    <div className="mx-auto max-w-md py-4">
      <Card>
        <div className="flex flex-col items-center text-center">
          {!token ? (
            <>
              <QrCode className="h-12 w-12 text-slate-400" />
              <h1 className="mt-3 text-lg font-semibold">{t('Scan the office door code')}</h1>
              <p className="mt-1 text-sm text-slate-500">{t('Open your phone camera and point it at the QR code on the office tablet.')}</p>
            </>
          ) : done ? (
            <>
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <h1 className="mt-3 text-xl font-semibold">
                {done.clockedIn
                  ? t('Clocked in at {time}', { time: fmt(done.lastPunch?.occurredAt, done.today.timezone, 'HH:mm') })
                  : t('Clocked out at {time}', { time: fmt(done.lastPunch?.occurredAt, done.today.timezone, 'HH:mm') })}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {done.duplicate ? (done.clockedIn ? t('You were already clocked in, so nothing changed.') : t('You had just scanned — the earlier scan was kept.')) : t('Recorded by {device}.', { device: done.deviceName })}
              </p>
              <Link to="/me/attendance" className="mt-5 text-sm font-medium text-brand-700 hover:underline">
                {t('View my attendance →')}
              </Link>
            </>
          ) : status.isLoading ? (
            <Spinner />
          ) : (
            <>
              <QrCode className="h-12 w-12 text-brand-600" />
              <h1 className="mt-3 text-lg font-semibold">{t('Office door')}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {status.data?.clockedIn ? t('You are currently clocked in.') : t('You are not clocked in yet.')}
              </p>
              <div className="mt-4 w-full">
                {expired ? (
                  <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{t('This code has expired. Scan the code on the tablet again.')}</p>
                ) : (
                  <ErrorBanner error={scan.error} />
                )}
              </div>
              {!expired && (
                <div className="mt-2 grid w-full grid-cols-2 gap-3">
                  <Button
                    className="py-4 text-base"
                    variant={status.data?.clockedIn ? 'secondary' : 'primary'}
                    icon={<LogIn className="h-5 w-5" />}
                    loading={scan.isPending && scan.variables === 'in'}
                    disabled={scan.isPending}
                    onClick={() => scan.mutate('in')}
                  >
                    {t('Clock in')}
                  </Button>
                  <Button
                    className="py-4 text-base"
                    variant={status.data?.clockedIn ? 'primary' : 'secondary'}
                    icon={<LogOut className="h-5 w-5" />}
                    loading={scan.isPending && scan.variables === 'out'}
                    disabled={scan.isPending}
                    onClick={() => scan.mutate('out')}
                  >
                    {t('Clock out')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
